import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import {
  readAccessTarget,
  searchAccessTargets,
  type AccessTarget,
  type AccessTargetInput,
} from "../../api/accessManagement";

const SEARCH_DELAY_MS = 300;

interface TargetPickerTarget {
  kind?: "member" | "technical";
  member_id: string | null;
  login_identity_id?: string | null;
  display_name: string;
  username: string | null;
  membership_category: string | null;
  status: string | null;
  active_roles?: string[];
  elected_eligibility?: { eligible: boolean; reasons: string[] };
}

interface AccessTargetPickerProps<TTarget extends TargetPickerTarget = AccessTarget> {
  accessToken: string;
  label: string;
  initialTarget?: AccessTargetInput;
  memberOnly?: boolean;
  maxQueryLength?: number;
  searchTargets?: (accessToken: string, query: string, memberOnly: boolean) => Promise<TargetPickerTarget[]>;
  readTarget?: (accessToken: string, input: AccessTargetInput) => Promise<TTarget>;
  onTargetChange: (target: TTarget | null) => void;
}

type SearchState =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "results"; results: Array<{ key: string; label: string; detail: string; input: AccessTargetInput }> }
  | { type: "empty" }
  | { type: "error" };

const ROLE_LABELS: Record<string, string> = {
  admin_tecnico: "Administração técnica",
  secretaria_daf: "Secretaria/DAF",
  marketing: "Marketing",
  diretoria: "Diretoria",
  conselho_deliberativo: "Conselho Deliberativo",
  conselho_fiscal: "Conselho Fiscal",
  associado: "Associado",
};

const ELIGIBILITY_LABELS: Record<string, string> = {
  not_associated: "O alvo não é associado.",
  member_not_active: "O associado não está ativo.",
  membership_not_effective: "O associado não é Efetivo.",
  effective_less_than_three_years: "A efetivação ainda não completou mais de três anos.",
  active_login_required: "O associado não possui login ativo.",
};

// The picker is a stateful composite control; its branches are render/state transitions rather than
// domain decisions. Domain authorization remains in the RPC contract.
// eslint-disable-next-line complexity
export function AccessTargetPicker<TTarget extends TargetPickerTarget = AccessTarget>({
  accessToken,
  label,
  initialTarget,
  memberOnly = false,
  maxQueryLength,
  searchTargets,
  readTarget,
  onTargetChange,
}: AccessTargetPickerProps<TTarget>) {
  const inputId = useId();
  const listboxId = `${inputId}-options`;
  const [query, setQuery] = useState("");
  const [searchState, setSearchState] = useState<SearchState>({ type: "idle" });
  const [selected, setSelected] = useState<TTarget | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [resolving, setResolving] = useState(false);
  const requestVersion = useRef(0);
  const resolveTarget: (accessToken: string, input: AccessTargetInput) => Promise<TTarget> =
    readTarget ?? ((token, input) => readAccessTarget(token, input) as unknown as Promise<TTarget>);

  function selectTarget(input: AccessTargetInput) {
    const version = ++requestVersion.current;
    setResolving(true);
    setSearchState({ type: "idle" });
    void resolveTarget(accessToken, input)
      .then((target) => {
        if (version !== requestVersion.current) return;
        setSelected(target);
        onTargetChange(target);
        setQuery("");
      })
      .catch(() => {
        if (version !== requestVersion.current) return;
        setSearchState({ type: "error" });
      })
      .finally(() => {
        if (version === requestVersion.current) setResolving(false);
      });
  }

  useEffect(() => {
    if (!initialTarget) return;
    selectTarget(initialTarget);
    // O alvo de rota é representado por valores primitivos para não reidratar a cada render do pai.
  }, [accessToken, initialTarget?.member_id, initialTarget?.username]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchState({ type: "idle" });
      setActiveIndex(-1);
      return;
    }

    const version = ++requestVersion.current;
    const timer = window.setTimeout(() => {
      setSearchState({ type: "loading" });
      const search = searchTargets
        ? searchTargets(accessToken, trimmed, memberOnly)
        : searchAccessTargets(accessToken, trimmed, memberOnly) as unknown as Promise<TargetPickerTarget[]>;
      void search
        .then((response) => {
          if (version !== requestVersion.current) return;
          const results = response.map((target) => ({
            key: `${target.kind}-${target.member_id ?? target.login_identity_id}`,
            label: target.display_name,
            input: target.member_id
              ? { member_id: target.member_id }
              : { username: target.username ?? "" },
            detail: target.kind === "technical"
              ? `Usuário técnico · ${target.username ?? ""}`
              : `${target.membership_category ?? ""} · ${target.status ?? ""}`,
          }));
          if (results.length === 0) {
            setSearchState({ type: "empty" });
          } else {
            setSearchState({ type: "results", results });
            setActiveIndex(0);
          }
        })
        .catch(() => {
          if (version === requestVersion.current) setSearchState({ type: "error" });
        });
    }, SEARCH_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      requestVersion.current += 1;
    };
  }, [accessToken, memberOnly, query]);

  const options = searchState.type === "results"
    ? searchState.results
    : [];

  function clearSelection() {
    requestVersion.current += 1;
    setSelected(null);
    onTargetChange(null);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" && options.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, options.length - 1));
    } else if (event.key === "ArrowUp" && options.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault();
      selectTarget(options[activeIndex].input);
    } else if (event.key === "Escape") {
      setSearchState({ type: "idle" });
      setActiveIndex(-1);
    }
  }

  return (
    <section className="access-target-picker" aria-label={label}>
      {selected ? (
        <div className="access-target-picker__selected" aria-live="polite">
          <div>
            <strong>{selected.display_name}</strong>
            {selected.username && <span>Usuário: {selected.username}</span>}
            {selected.membership_category && selected.status && <span>{selected.membership_category} · {selected.status}</span>}
            {selected.active_roles && <span>Perfis ativos: {selected.active_roles.map((role) => ROLE_LABELS[role] ?? role).join(", ") || "Nenhum"}</span>}
            {selected.elected_eligibility && (selected.elected_eligibility.eligible ? (
              <span>Elegível para cargo eletivo.</span>
            ) : (
              <span>{selected.elected_eligibility.reasons.map((reason) => ELIGIBILITY_LABELS[reason] ?? reason).join(" ")}</span>
            ))}
          </div>
          <button type="button" className="btn btn--secondary" onClick={clearSelection}>Trocar alvo</button>
        </div>
      ) : (
        <>
          <label htmlFor={inputId}>{label}</label>
          <input
            id={inputId}
            type="search"
            role="combobox"
            value={query}
            autoComplete="off"
            maxLength={maxQueryLength}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={options.length > 0}
            aria-activedescendant={activeIndex >= 0 ? `${inputId}-option-${activeIndex}` : undefined}
            aria-describedby={`${inputId}-help`}
            placeholder={memberOnly ? "Pesquise pelo nome" : "Nome do associado ou usuário técnico"}
            onChange={(event) => {
              clearSelection();
              setQuery(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            disabled={resolving}
          />
          <p id={`${inputId}-help`}>Digite ao menos duas letras e escolha um resultado. Identificadores internos não são necessários.</p>
          {resolving && <p role="status" aria-live="polite">Carregando alvo…</p>}
          {searchState.type === "loading" && <p role="status" aria-live="polite">Pesquisando associados…</p>}
          {searchState.type === "empty" && <p role="status" aria-live="polite">Nenhum associado encontrado.</p>}
          {searchState.type === "error" && <p className="field__error" role="status">Não foi possível consultar o alvo. Tente novamente.</p>}
          {options.length > 0 && (
            <ul id={listboxId} className="access-target-picker__options" role="listbox" aria-label={`${label}: resultados`}>
              {options.map((option, index) => (
                <li
                  key={option.key}
                  id={`${inputId}-option-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={index === activeIndex ? "access-target-picker__option access-target-picker__option--active" : "access-target-picker__option"}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectTarget(option.input);
                  }}
                >
                  <strong>{option.label}</strong>
                  <span>{option.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
